import { useEffect, useMemo } from 'react';
import { Outlet, ScrollRestoration, useLocation, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Theme } from '@astryxdesign/core/theme';
import { LinkProvider } from '@astryxdesign/core/Link';
import { ToastViewport } from '@astryxdesign/core/Toast';
import { RouterLink } from '@/components/RouterLink';
import { FullPageError, FullPageLoading } from '@/components/StateViews';
import { hideBootSplash } from '@/lib/boot';
import { ApiError } from '@/lib/errors';
import { env } from '@/lib/env';
import { registerTenantServiceWorker } from '@/features/pwa/registerSW';
import { tenantQuery } from './api';
import { applyTenantHead } from './head';
import { useThemeMode } from '@/features/theme/mode';
import { BODY_BACKGROUND, fallbackTheme, tenantTheme } from './theme';
import type { TenantContext } from './context';

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])$/;

export function TenantLayout() {
  const { slug = '' } = useParams();
  const { pathname } = useLocation();
  const area = pathname.includes('/owner') ? 'owner' : 'client';
  const valid = SLUG_RE.test(slug);
  const q = useQuery({ ...tenantQuery(slug), enabled: valid && env.ok });

  const theme = useMemo(() => (q.data ? tenantTheme(q.data.slug, q.data.branding.accent_color) : fallbackTheme), [q.data]);

  const { mode, scheme } = useThemeMode();

  useEffect(() => {
    if (q.data) applyTenantHead(q.data, area, scheme === 'dark' ? q.data.branding.theme_color : BODY_BACKGROUND.light, scheme);
  }, [q.data, area, scheme]);

  useEffect(() => {
    if (valid) registerTenantServiceWorker(slug);
  }, [slug, valid]);

  // The HTML shell paints the studio's logo at once; it stays up until there is something to show.
  const loading = env.ok && valid && q.isPending;
  useEffect(() => {
    if (!loading) hideBootSplash();
  }, [loading]);

  let content;
  if (!env.ok) content = <FullPageError title="Приложение не настроено" description={`Не заданы переменные окружения: ${'error' in env ? env.error : ''}`} />;
  else if (!valid) content = <FullPageError title="Студия не найдена" description="Проверьте адрес ссылки." />;
  // Known studio data (fresh or from this device) keeps the app usable when a background refresh fails.
  else if (q.data && !(q.error instanceof ApiError && q.error.code === 'tenant_not_found')) content = <Outlet context={{ tenant: q.data } satisfies TenantContext} />;
  else if (q.isPending) content = <FullPageLoading label="Загружаем студию…" />;
  else content = <FullPageError title="Не удалось загрузить студию" error={q.error} onRetry={() => q.refetch()} />;

  return (
    <Theme theme={theme} mode={mode}>
      <LinkProvider component={RouterLink}>
        {content}
        {/* New page → top; the booking sheet opens over the home page without moving it. */}
        <ScrollRestoration getKey={(location) => location.pathname.replace(/\/booking\/?$/, '').replace(/\/$/, '')} />
        <ToastViewport position="topEnd" />
      </LinkProvider>
    </Theme>
  );
}
